import api from 'api'

/** What a partner asked for, exactly as it arrived on the consent page. */
export interface PartnerConsentRequest {
	clientId: string
	redirectUri: string
	scope: string
	state: string | null
}

export interface PartnerConsentDescription {
	applicationName: string
	/** Media key relative to VITE_MEDIA_URL. Absent shows the partner's initial: the API leaves out empty fields. */
	logo?: string | null
	/** A phrase completing "[partner] can see …, and", such as "may use them to recommend events to you". */
	dataUseStatement?: string | null
	permissions: string[]
}

export interface PartnerConsentProblem {
	error: string
	message: string
}

export type PartnerConsentResult<T> = { ok: true; value: T } | { ok: false; problem: PartnerConsentProblem }

export const PARTNER_CONSENT_UNAVAILABLE: PartnerConsentProblem = {
	error: 'unavailable',
	message: 'Something went wrong. Please try again.',
}

// The shared api instance resolves a refused non-auth request as undefined, dropping its error code. These calls take
// refusals as responses instead, so the page can tell a bad link from a paused connection.
const acceptRefusals = { validateStatus: (status: number) => status < 500 && status !== 401 }

const isRefusal = (body: unknown): body is PartnerConsentProblem => !!body && typeof body === 'object' && 'error' in body

/** By default a response is an object or list; endpoints answering otherwise say what they accept. */
const isAnswer = (body: unknown) => !!body && typeof body === 'object' && !isRefusal(body)

const call = async <T>(
	send: () => Promise<unknown>,
	accepts: (body: unknown) => boolean = isAnswer
): Promise<PartnerConsentResult<T>> => {
	try {
		const body = await send()
		if (accepts(body)) return { ok: true, value: body as T }
		return { ok: false, problem: isRefusal(body) ? body : PARTNER_CONSENT_UNAVAILABLE }
	} catch {
		return { ok: false, problem: PARTNER_CONSENT_UNAVAILABLE }
	}
}

export const describePartnerConsent = (request: PartnerConsentRequest) =>
	call<PartnerConsentDescription>(() =>
		api.get('/partner-consent', {
			params: {
				client_id: request.clientId,
				redirect_uri: request.redirectUri,
				scope: request.scope,
				state: request.state ?? undefined,
			},
			...acceptRefusals,
		})
	)

export const approvePartnerConsent = (request: PartnerConsentRequest) =>
	call<{ redirectUrl: string }>(() => api.post('/partner-consent/approve', request, acceptRefusals))

export const declinePartnerConsent = (request: PartnerConsentRequest) =>
	call<{ redirectUrl: string }>(() => api.post('/partner-consent/decline', request, acceptRefusals))

/** A partner as the account holder sees it under Connected Partners. */
export interface ConnectedPartner {
	clientId: string
	name: string
	/** Media key relative to VITE_MEDIA_URL. Absent shows the partner's initial: the API leaves out empty fields. */
	logo?: string | null
	/** "Disabled" is shown as paused. Disconnected partners are not listed. */
	status: 'Active' | 'Disabled'
	permissions: string[]
	/** When the account holder last agreed: their latest connect or approval. */
	connectedAt: string
	changedAt: string
}

const connection = (clientId: string, action: 'enable' | 'disable' | 'unlink') =>
	`/partner-consent/connections/${encodeURIComponent(clientId)}/${action}`

export const listConnectedPartners = () =>
	call<ConnectedPartner[]>(() => api.get('/partner-consent/connections', acceptRefusals))

export const resumePartnerConnection = (clientId: string) =>
	call<ConnectedPartner>(() => api.post(connection(clientId, 'enable'), null, acceptRefusals))

export const pausePartnerConnection = (clientId: string) =>
	call<ConnectedPartner>(() => api.post(connection(clientId, 'disable'), null, acceptRefusals))

/** The partner a confirmation's disconnect link names. Opening the link changes nothing; only disconnecting does. */
export const describePartnerDisconnect = (token: string) =>
	call<ConnectedPartner>(() => api.get('/partner-consent/disconnect', { params: { token }, ...acceptRefusals }))

/** Disconnects the partner a confirmation's link names. Holding the link is the proof, so no sign-in is needed. */
export const disconnectPartnerByLink = (token: string) =>
	call<true>(
		() => api.post('/partner-consent/disconnect', { token }, acceptRefusals),
		body => body === true
	)

/** Revokes the connection. Reconnecting means approving again on the consent page. */
export const disconnectPartner = (clientId: string) =>
	call<true>(
		() => api.post(connection(clientId, 'unlink'), null, acceptRefusals),
		body => body === true
	)
